import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, PlusCircle, Loader2, MapPin, Building2, Check, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';

const LocationSetup = () => {
  const [locations, setLocations] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = `${window.location.protocol}//${window.location.hostname}/api/locations`;
  const CLINICS_API_URL = `${window.location.protocol}//${window.location.hostname}/api/clinics`;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [locRes, clinicsRes] = await Promise.all([
        axios.get(API_URL, { headers }),
        axios.get(CLINICS_API_URL, { headers })
      ]);
      setLocations(locRes.data);
      setClinics(clinicsRes.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdate = async (id, updatedData) => {
    const toastId = toast.loading('Updating location...');
    try {
      await axios.put(`${API_URL}/${id}`, updatedData, { headers });
      toast.success('Location updated!', { id: toastId });
      fetchData();
    } catch (err) {
      toast.error('Failed to update location', { id: toastId });
    }
  };

  const deleteLocation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    const toastId = toast.loading('Deleting location...');
    try {
      await axios.delete(`${API_URL}/${id}`, { headers });
      toast.success('Location deleted!', { id: toastId });
      fetchData();
    } catch (err) {
      toast.error('Failed to delete location', { id: toastId });
    }
  };

  const addLocation = async () => {
    const toastId = toast.loading('Adding new location...');
    try {
      const newLoc = {
        code: `LOC-${locations.length + 101}`,
        name: 'New Department Location',
        active: true,
        clinicId: clinics.length > 0 ? clinics[0].id : null,
        allBranches: true,
        branches: []
      };
      await axios.post(API_URL, newLoc, { headers });
      toast.success('New location added!', { id: toastId });
      fetchData();
    } catch (err) {
      toast.error('Failed to add location', { id: toastId });
    }
  };

  if (loading && locations.length === 0) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
    </div>
  );

  return (
    <div className="p-6 bg-[#F8FAFC] min-h-screen text-slate-700 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Compact Glass Header */}
        <div className="bg-white px-8 py-6 rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/40 flex justify-between items-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-500" />
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-200">
                    <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                    <h1 className="text-2xl font-black text-slate-800 tracking-tight">Location Setup</h1>
                    <p className="text-[11px] text-slate-400 font-black uppercase tracking-widest mt-1">Manage hospital rooms and departments</p>
                </div>
            </div>
            <button 
                onClick={addLocation}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-black text-white rounded-2xl text-[12px] font-black uppercase tracking-widest shadow-xl shadow-emerald-100 transition-all active:scale-95 group"
            >
                <PlusCircle className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                Add New Location
            </button>
        </div>

        {/* Dense Table View */}
        <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden text-[12px]">
          <table className="min-w-full divide-y divide-slate-100 text-left">
            <thead className="bg-slate-50/50">
              <tr>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest">Identify Code</th>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest">Active</th>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest">Location Name / Label</th>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-center">Facility Clinic</th>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {locations.map((item) => (
                <tr key={item.id} className="hover:bg-emerald-50/10 transition-all group">
                  <td className="px-6 py-3">
                    <input 
                      type="text" 
                      value={item.code} 
                      className="w-24 px-3 py-2 bg-slate-100/50 border border-slate-200 rounded-xl text-[11px] font-black text-emerald-700 uppercase tracking-wider focus:outline-none focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-500 focus:bg-white transition-all text-center"
                      onBlur={(e) => handleUpdate(item.id, { code: e.target.value })}
                      onChange={(e) => {
                         const val = e.target.value;
                         setLocations(locations.map(t => t.id === item.id ? { ...t, code: val } : t));
                      }}
                    />
                  </td>
                  <td className="px-6 py-3">
                    <button 
                        onClick={() => handleUpdate(item.id, { active: !item.active })}
                        className={`relative inline-flex h-5 w-10 items-center rounded-full transition-all duration-300 ${
                            item.active ? 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.3)]' : 'bg-slate-200'
                        }`}
                    >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition duration-300 ${
                            item.active ? 'translate-x-5.5' : 'translate-x-1'
                        }`} />
                    </button>
                  </td>
                  <td className="px-6 py-3 min-w-[250px]">
                    <input 
                        type="text" 
                        value={item.name} 
                        className="w-full px-4 py-2.5 bg-white border border-transparent rounded-xl text-[13px] font-bold text-slate-700 focus:outline-none focus:border-emerald-100 focus:bg-emerald-50/10 transition-all group-hover:border-slate-100"
                        onBlur={(e) => handleUpdate(item.id, { name: e.target.value })}
                        onChange={(e) => {
                            const val = e.target.value;
                            setLocations(locations.map(t => t.id === item.id ? { ...t, name: val } : t));
                        }}
                    />
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center justify-center">
                        <div className="relative group/sel">
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-300" />
                            <select 
                                className="pl-8 pr-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black text-slate-500 focus:outline-none focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-200 appearance-none min-w-[160px]"
                                value={item.clinicId || ''}
                                onChange={(e) => handleUpdate(item.id, { clinicId: e.target.value })}
                            >
                                <option value="">Select Clinic...</option>
                                {clinics.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => deleteLocation(item.id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {locations.length === 0 && (
             <div className="py-20 text-center space-y-3">
                 <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto text-slate-200">
                     <MapPin className="w-8 h-8" />
                 </div>
                 <div className="text-slate-300 font-black uppercase tracking-widest text-[10px]">No locations configured yet</div>
             </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationSetup;
